import LoadingGif from "@src/app/assets/lottie/loading.gif";
import { ThemeEnum } from "@src/app/components//ThemeProvider/ThemeProvider";
import useGAData from "@src/app/hooks/useGAData";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import { useIsArticlePage } from "@src/app/hooks/useIsArticlePage";
import { useRouteResponse } from "@src/app/hooks/useRouteResponse";
import type { BFFArticleCard, BFFResponseType } from "@src/app/types/bff";
import type {
  AuthorPageResponse,
  KeywordsPageResponse,
  ListingPageResponse,
  VideoPageResponse,
} from "@src/app/types/response";
import cx from "classnames";
import { useEffect, useRef } from "react";

import VerticalCard from "../ArticleCard/VerticalCard";
import SectionContainer from "../SectionContainer";
import type { IInfiniteScollListRef } from "./InfiniteScollList";
import InfiniteScollList from "./InfiniteScollList";

function LoadingMore({
  loading = false,
  hasMore = true,
  theme = ThemeEnum.light,
}: {
  loading?: boolean;
  hasMore?: boolean;
  theme?: ThemeEnum;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 px-3 py-4">
        <img src={LoadingGif} alt="" className="h-16" />
      </div>
    );
  }

  if (!hasMore) {
    return (
      <div
        className={cx(
          "text-center text-[14px] py-4 px-3 font-[500]",
          theme === ThemeEnum.dark
            ? "text-secondary-on-dark"
            : "text-secondary",
        )}
      >
        已显示全部内容
      </div>
    );
  }

  return null;
}

type ResponseType =
  | ListingPageResponse
  | KeywordsPageResponse
  | VideoPageResponse
  | AuthorPageResponse;

/**
 * @description will be used in KeywordsPage, AuthorPage, VideoPage, ListingPage
 */
export default function PageArticleList({
  title = "",
  requestPath = "",
  requestQuery = {},
  selectedArticleId = "",
  isVideoPlaying = false,
  isVideoPaused = false,
  theme = ThemeEnum.light,
  loadMore = false,
  updatedData = () => {},
  showHeader = true,
  showSubHeader = true,
  className = "",
  initialData = [],
}: {
  title?: string;
  requestPath: string;
  requestQuery?: Record<string, string | number>;
  selectedArticleId?: string;
  isVideoPlaying?: boolean;
  isVideoPaused?: boolean;
  theme?: ThemeEnum;
  loadMore?: boolean;
  updatedData?: (data: BFFArticleCard[]) => void;
  showHeader?: boolean;
  showSubHeader?: boolean;
  className?: string;
  initialData: BFFArticleCard[];
}) {
  const sendDataToGTM = useGTMDispatch();
  const ref = useRef<IInfiniteScollListRef>(null);

  useEffect(() => {
    const loadMoreFunction = ref.current?.loadMore;

    if (loadMore && loadMoreFunction) {
      void loadMoreFunction();
    }
  }, [loadMore]);

  const {
    gaLevel2,
    chapter1,
    contenttype,
    author,
    contentcat,
    articleid,
    pubdate,
  } = useGAData();

  const isArticlePage = useIsArticlePage();
  const { article } = useRouteResponse("articlePage");

  return (
    <SectionContainer
      title={title}
      theme={theme}
      showHeader={showHeader}
      className={className}
    >
      <InfiniteScollList<BFFArticleCard>
        ref={ref}
        initialData={initialData}
        onChange={(_data) => {
          console.log(_data);
          updatedData(_data);
        }}
        renderLoadingMore={(loading, hasMore) => (
          <LoadingMore loading={loading} hasMore={hasMore} theme={theme} />
        )}
        pageSize={8}
        fetchData={async ({ page, pageSize }) => {
          // when the article card is loaded or user scroll to the next section
          // TODO: add more info in Video Article Page
          page > 1 &&
            sendDataToGTM({
              event: "virtual_pv",
              articleid,
              cur_articleid: articleid,
              pagination: page,
              author,
              level2: gaLevel2,
              chapter1,
              contentcat,
              contenttype,
              pubdate,
              story_threads: "",
              title: article?.title || "",
              virtual_url: isArticlePage ? `article/${articleid}` : "",
            });
          const params = { page, pageSize };
          const queryParams = Object.entries(requestQuery)
            .map(([key, value]) => `${key}=${value}`)
            .join("&");

          const paramStr = `?page=${params.page}&pageSize=${params.pageSize}&${queryParams}`;
          const res = await fetch(
            `/_plat/api/v1/client${requestPath}${paramStr}`,
          );
          const data = (await res.json()) as BFFResponseType<ResponseType>;
          return data.response.articles || [];
        }}
        className="grid grid-cols-12"
        renderItem={(article, index) => {
          return (
            <VerticalCard
              key={article.id + index}
              className="col-span-12 lg:col-span-3"
              article={article}
              theme={theme}
              isVideoPaused={selectedArticleId == article.id && isVideoPaused}
              isVideoPlaying={selectedArticleId == article.id && isVideoPlaying}
              showSubHeader={showSubHeader}
            />
          );
        }}
      />
    </SectionContainer>
  );
}
